import React, { useState } from 'react';
import { useQuery } from 'react-query';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import OccupantService, { OccupantProps } from 'services/hooks/occupant-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import { Table } from '../Common/Table';
import Save from './OccupantForms/save';
import Delete from './OccupantForms/delete';
import { FilterSelect } from 'utils/FilterSelectUtils';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import ImportCSV from './OccupantForms/importCSV';
import ExportCSV from './OccupantForms/exportCSV';

export default function OccupantsSaveMode() {
  const [getOccupantsResult, setGetOccupantsResult] = useState<OccupantProps[] | null>([]);
  const [orgList, setOrgList] = useState<OrganizationProps[] | null>();
  const {
    isLoading: isLoadingOccupants,
    refetch,
    data: occupantsData
  } = useQuery<OccupantProps[], Error>('occupants', OccupantService.findAllOccupants, {
    onError: (err: any) => {
      console.log(err.response?.data || err);
    }
  });

  const { refetch: orgRefetch, data: OrgData } = useQuery<OrganizationProps[], Error>(
    'organizations',
    OrganizationService.findAllOrgs,
    {
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  React.useEffect(() => {
    if (occupantsData && Array.isArray(occupantsData)) {
      refetch();
      setGetOccupantsResult(occupantsData);
    }
    if (OrgData && Array.isArray(OrgData)) {
      orgRefetch();
      setOrgList(OrgData);
    }
  }, [OrgData, occupantsData, orgRefetch, refetch]);

  const columns = React.useMemo(
    () => [
      {
        id: 'fullname',
        Header: 'Full Name',
        accessor: (row: { firstName: string; lastName: string }) => {
          return `${row?.firstName} ${row?.lastName}`;
        },
        width: 500,
        disableFilters: true
      },
      {
        id: 'organization',
        Header: 'Organization',
        accessor: (row: { organizationUuid: string }) => {
          return orgList?.find((f: { uuid: string }) => f?.uuid === row?.organizationUuid)?.name;
        },
        Filter: FilterSelect
      },
      {
        id: 'phonenumber',
        Header: 'Phone Number',
        accessor: (row: { phoneNumber: string }) => {
          return `${row?.phoneNumber}`;
        },
        disableFilters: true,
        style: { width: '50px' }
      },
      {
        id: 'status',
        Header: 'Card',
        accessor: (row: { cards: { status: string; badgeId: string }[] }) => {
          if (row?.cards?.length > 0) {
            return row?.cards?.map((card: { status: string; badgeId: string }, index: number) => (
              <div className="text-left text-sm" key={index}>
                {card?.status}{' '}
                {card?.status === 'ASSIGNED' && (
                  <span>
                    | Card No: <b> {card?.badgeId} </b>
                  </span>
                )}
              </div>
            ));
          } else {
            return <div className="text-left text-sm">UNASSIGNED</div>;
          }
        },
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: any) => data,
        Cell: (row: any) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    [orgList]
  );

  if (isLoadingOccupants || !getOccupantsResult) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return !isLoadingOccupants && getOccupantsResult ? (
    <div className="occupants">
      <Table
        name="Occupants"
        columns={columns}
        data={getOccupantsResult}
        refetch={refetch}
        CreateForm={Save}
        DeleteForm={Delete}
        ImportForm={ImportCSV}
        ExportForm={ExportCSV}
        createQuery={OccupantService.createOccupant}
        updateQuery={OccupantService.updateOccupant}
        deleteQuery={OccupantService.deleteOccupant}
        editQuery={OccupantService.findOccupantsById}
        importQuery={OccupantService.importOccupantsCSV}
        queryName="occupants"
        buttonName="Occupant"
        toolTipMessage="Occupants are permanent B-Line users who can book amenities and receive digital access cards."
      />
    </div>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
